import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type RowClassParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./cell_monitoring.scoped.scss";

interface State {
}

const defState: State = {
};

interface SearchParams {
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const gridInfo: (CustomColDef | CustomColGroupDef)[] = [
  { headerName: "구분", field: "typeName", width: 80, cellStyle: {textAlign: "center"}, summaryLabel: "합계" },
  { headerName: "총셀수", headerClass: "hd-ta-right", field: "totalCellQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "사용셀수", headerClass: "hd-ta-right", field: "totalCellUseQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "금지셀수", headerClass: "hd-ta-right", field: "disableCellQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "사용률(%)", headerClass: "hd-ta-right", field: "useRate", width: 110, cellStyle: { textAlign: "right"},
    valueGetter: (p: any) => {
      return valueFormatter.percentFormatter(p.data.totalCellQty, p.data.totalCellUseQty, {
        scale: 1,
        default: "-",
        method: "FLOOR"
      });
    },
  },
  { headerName: "SKU수량", headerClass: "hd-ta-right", field: "skuQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "SKU셀", headerClass: "hd-ta-right", field: "skuCellQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "SKU사용셀", headerClass: "hd-ta-right", field: "skuCellUseQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "SKU미사용셀", headerClass: "hd-ta-right", field: "skuCellNoUseQty", width: 110, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
  { headerName: "SKU저장률(%)", headerClass: "hd-ta-right", field: "skuSaveRate", width: 110, cellStyle: { textAlign: "right"},
    valueGetter: (p: any) => {
      return valueFormatter.percentFormatter(p.data.skuCellQty, p.data.skuCellUseQty, {
        scale: 1,
        default: "-",
        method: "FLOOR"
      });
    },
  },
  {
    headerName: "대형SKU",
    children: [
      { headerName: "수량", headerClass: "hd-ta-right", field: "qty11", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "전체셀", headerClass: "hd-ta-right", field: "qty12", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "사용셀", headerClass: "hd-ta-right", field: "qty13", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "미사용셀", headerClass: "hd-ta-right", field: "qty14", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "SKU저장률(%)", headerClass: "hd-ta-right", field: "qty15", width: 100, cellStyle: { textAlign: "right"},
        valueGetter: (p: any) => {
          return valueFormatter.percentFormatter(p.data.qty12, p.data.qty13, {
            scale: 1,
            default: "-",
            method: "FLOOR"
          });
        },
      },
    ]
  },
  {
    headerName: "중형SKU",
    children: [
      { headerName: "수량", headerClass: "hd-ta-right", field: "qty21", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "전체셀", headerClass: "hd-ta-right", field: "qty22", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "사용셀", headerClass: "hd-ta-right", field: "qty23", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "미사용셀", headerClass: "hd-ta-right", field: "qty24", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "SKU저장률(%)", headerClass: "hd-ta-right", field: "qty25", width: 100, cellStyle: { textAlign: "right"},
        valueGetter: (p: any) => {
          return valueFormatter.percentFormatter(p.data.qty22, p.data.qty23, {
            scale: 1,
            default: "-",
            method: "FLOOR"
          });
        },
      },
    ]
  },
  {
    headerName: "소형SKU",
    children: [
      { headerName: "수량", headerClass: "hd-ta-right", field: "qty31", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "전체셀", headerClass: "hd-ta-right", field: "qty32", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "사용셀", headerClass: "hd-ta-right", field: "qty33", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "미사용셀", headerClass: "hd-ta-right", field: "qty34", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "SKU저장률(%)", headerClass: "hd-ta-right", field: "qty35", width: 100, cellStyle: { textAlign: "right"},
        valueGetter: (p: any) => {
          return valueFormatter.percentFormatter(p.data.qty32, p.data.qty33, {
            scale: 1,
            default: "-",
            method: "FLOOR"
          });
        },
      },
    ]
  },
  {
    headerName: "초소형SKU",
    children: [
      { headerName: "수량", headerClass: "hd-ta-right", field: "qty41", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "전체셀", headerClass: "hd-ta-right", field: "qty42", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "사용셀", headerClass: "hd-ta-right", field: "qty43", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "미사용셀", headerClass: "hd-ta-right", field: "qty44", width: 100, cellStyle: { textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
      { headerName: "SKU저장률(%)", headerClass: "hd-ta-right", field: "qty45", width: 100, cellStyle: { textAlign: "right"},
        valueGetter: (p: any) => {
          return valueFormatter.percentFormatter(p.data.qty42, p.data.qty43, {
            scale: 1,
            default: "-",
            method: "FLOOR"
          });
        },
      },
    ]
  }
];

function ZoneInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = {
  };
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/zone_info.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/zone_info", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/zone_info", searchParams);
  }, [result, router, updateSearchParams]);

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/zone_info.download?mode=search", searchParams, {
        "fileName": "상세구획정보_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>상세 구획 정보</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 상세 구획 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 상세 구획 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(ZoneInfo);
